import React, { useCallback, useMemo, useState } from 'react';
import { Flex, Heading, Hide, Show } from '@chakra-ui/react';
import ResponsiveButtonGroup from 'ts/common/components/ResponsiveButtonGroup';
import { ButtonProps } from 'ts/common/components/gallery';

interface Props {
    backButtonText: string;
    billingInformationFields: React.ReactNode;
    billingInformationTitle: string;
    isSubmitDisabled?: boolean;
    isSubmitLoading?: boolean;
    onClickBack: () => void;
    onSubmit: (event: React.FormEvent) => Promise<void>;
    paymentOptions: React.ReactNode;
    paymentSummary: React.ReactNode;
    tipSection?: React.ReactNode;
    submitButtonText: string;
}

const ClientPaymentForm = ({
    backButtonText,
    billingInformationFields,
    billingInformationTitle,
    isSubmitDisabled,
    isSubmitLoading,
    onClickBack,
    onSubmit,
    paymentOptions,
    paymentSummary,
    tipSection,
    submitButtonText
}: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault();
            setIsSubmitting(true);

            try {
                await onSubmit(event);
            } finally {
                setIsSubmitting(false);
            }
        },
        [onSubmit]
    );

    const buttonsConfig = useMemo<Array<ButtonProps>>(
        () => [
            {
                text: backButtonText,
                variant: 'outline primary',
                onClick: onClickBack
            },
            {
                text: submitButtonText,
                variant: 'primary',
                isDisabled: isSubmitting || isSubmitDisabled,
                isLoading: isSubmitting || isSubmitLoading,
                type: 'submit'
            }
        ],
        [
            backButtonText,
            isSubmitting,
            isSubmitDisabled,
            isSubmitLoading,
            onClickBack,
            submitButtonText
        ]
    );

    return (
        <Flex
            as="form"
            alignItems="stretch"
            flexDirection="column"
            gap="40px"
            onSubmit={handleSubmit}
        >
            <Flex gap="24px 64px">
                <Flex flex="1 1 auto" flexDirection="column" gap="24px 64px">
                    {paymentOptions}
                    {tipSection}
                    <Hide above="lg">{paymentSummary}</Hide>
                    <Flex flexDirection="column" gap="16px">
                        <Heading as="h3" size="lg" margin={0}>
                            {billingInformationTitle}
                        </Heading>
                        {billingInformationFields}
                    </Flex>
                </Flex>
                <Show above="lg">{paymentSummary}</Show>
            </Flex>
            <ResponsiveButtonGroup buttons={buttonsConfig} marginTop="32px" />
        </Flex>
    );
};

export default ClientPaymentForm;
